import { AxiosResponse } from 'axios';
import PrescriptionInterface, { PrescriptionItemInterface } from '../interfaces/prescription';
import api from './api';
import { getItem } from './localSorage';
import replaceHtmlEntites from './replaceHtmlEntites';

export interface FetchPrescriptionResponse {
  prescription: any;
}

export const fetch = (token: string): Promise<PrescriptionInterface> => {
  const uri = `/dispenser/prescription/${token}?token=${getItem('token')}`;

  return api.get(uri).then((response: AxiosResponse<FetchPrescriptionResponse>) => {
    return normalize(response.data);
  });
};

function getAvailableQuantity(item: PrescriptionItemInterface) {
  const maxQuantity = Math.max(item.quantity || 0, 0);
  const dispensedQuantity = item.dispensation?.data?.reduce((sum, d) => sum + (d.quantity || 0), 0) || 0;
  return Math.max(maxQuantity - dispensedQuantity, 0);
}

export const dispense = (prescription: PrescriptionInterface): Promise<PrescriptionInterface | string[]> => {
  const token = getItem('token');
  const uri = `/dispenser?token=${token}`;

  const checkedItems = prescription.items
    .filter((item: PrescriptionItemInterface) => item.checked)
    .map((item: PrescriptionItemInterface) => {
      const availableQuantity = getAvailableQuantity(item);
      return {
        id: item.id,
        quantity: item.selectedQuantity === 'all' ? availableQuantity : item.selectedQuantity,
      };
    });

  const payload = {
    prescription_token: prescription.token,
    items: checkedItems,
  };

  return api.post(uri, payload).then(
    (response: AxiosResponse<FetchPrescriptionResponse>): PrescriptionInterface => {
      return normalize(response.data);
    },
    (error: any): Promise<string[]> => {
      const errors = Object.entries(error.response.data.errors).map(([field, err]) => {
        return err as string;
      });

      return Promise.reject(errors);
    },
  );
};

export const cancelDispensationItem = async (id: number, prescriptionToken: string): Promise<PrescriptionInterface> => {
  try {
    const authToken = getItem('token');
    const payload = {
      prescription_token: prescriptionToken,
      dispensation_ids: [id],
    };
    const uri = `dispenser/cancel?token=${authToken}`;

    const response = await api.post(uri, payload);
    return normalize(response.data);
  } catch (error) {
    return Promise.reject(error);
  }
};

const normalize = (response: FetchPrescriptionResponse): PrescriptionInterface => {
  const { prescription } = response;

  const data = {
    id: prescription.id,
    token: prescription.token,
    config: prescription.config,
    sent_via: prescription.sent_via,
    signed: prescription.isSigned,
    doctor: {
      id: prescription.doctor.id,
      abbreviation: prescription.doctor.abbreviation,
      name: prescription.doctor.name,
      city: prescription.doctor.city,
      regional_code: prescription.doctor.regional_code,
      board_number: prescription.doctor.board_number,
      board_state: prescription.doctor.board_state,
      uf: prescription.doctor.uf,
    },
    prescription_date: prescription.prescription_date,
    created_at: prescription.created_at,
    document: {
      url: prescription.document?.url ?? null,
      pdf: prescription.document?.pdf ?? null,
      pdf_access: prescription.document?.pdf_access ?? null,
    },
    patient: {
      name: prescription.patient.name,
      social_name: prescription.patient?.social_name,
    },
    items: normalizeItems(prescription.items),
    print_config: prescription.print_config,
  };

  return data;
};

function getItemCid(raw: any): { code: string; description: string } | undefined {
  const code = raw.cid?.trim();
  const description = raw.cid_description?.trim();
  if (!code || !description) {
    return undefined;
  }
  return { code, description };
}

const normalizeItems = (items: any): PrescriptionItemInterface[] => {
  const flattenItems: PrescriptionItemInterface[] = [
    ...items.customized.data,
    ...items.industrialized.data,
    ...items.manipulated.data,
  ];

  return flattenItems
    .filter((item) => {
      return item.original_quantity !== null;
    })
    .map((item: any) => {
      const dispensation = item.dispensation.data || [];
      const cid = getItemCid(item);

      return {
        id: item.id,
        name: item.compositions?.exist ? 'Fórmula' : item.name,
        dosage: replaceHtmlEntites(item.dosage.replace(/(<([^>]+)>)/gi, '')),
        dispensed: dispensation[0]?.status === 'unknown',
        checked: false,
        blocked: dispensation[0]?.status === 'blocked',
        original_quantity: item.original_quantity,
        isPartialDispensationEnabled: item.isPartialDispensationEnabled,
        quantity: item.quantity,
        unit: item.unit,
        specialControl: item.special_control,
        recipe: item.recipe,
        compositions: item.compositions,
        cid,
        dispensation: {
          data: dispensation.map((dispensationItem: any) => {
            return {
              id: dispensationItem.id,
              drugstore: dispensationItem.drugstore,
              // foi necessario realizar esse replace para compatibilidade com o ie11
              date: new Date(dispensationItem.created_at.replace(' ', 'T')),
              status: dispensationItem.status,
              quantity: dispensationItem.quantity,
            };
          }),
          document: {
            url: item.dispensation.document?.url ?? null,
            pdf_access: item.dispensation.document?.pdf_access ?? null,
          },
        },
      };
    });
};

interface UnlockResponse {
  doctor: {
    name: string;
    crm: string;
    uf: string;
  };
  patient: {
    name: string;
  };
}

export const unlock = (digits: string, prescriptionToken: string): Promise<UnlockResponse> => {
  const uri = '/patients/prescriptions/clarify';

  return api
    .get(uri, {
      params: {
        digits,
        token: prescriptionToken,
      },
    })
    .then((response: AxiosResponse<UnlockResponse>) => response.data);
};
